import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Slide } from '@material-ui/core';
import { ChatWindow } from './ChatWindow';
import { ExperienceBuilderView } from './ExperienceBuilderView';
import { useCopilotChat, type ToolAction } from './useCopilotChat';
import { useTemplatePreview } from './useTemplatePreview';
import { useCopilotMaybe } from './CopilotContext';
import { useCopilotPageBehavior } from './useCopilotPageBehavior';
import { useRouter } from '../../hooks/useRouter';
import { useRoutes } from '../../routes';
import { useAIObservability } from '../../services/productAnalytics/aiObservabilityEvents';

interface DadoCopilotProps {
  organizationId: string;
}

export const DadoCopilot: React.FC<DadoCopilotProps> = ({ organizationId }) => {
  // Open state lives in CopilotContext so the top-nav button can drive it.
  // Falls back to local state if the provider isn't mounted (defensive).
  const ctx = useCopilotMaybe();
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const isOpen = ctx ? ctx.isOpen : localIsOpen;
  const [lastActions, setLastActions] = useState<ToolAction[]>([]);
  const { history } = useRouter();
  const { ROUTES } = useRoutes();

  // Push-content + auto-close-on-modal behaviors, scoped per page.
  useCopilotPageBehavior();

  // Detect if user is on an experience template page (/experiences/:id)
  const pageContext = useMemo(() => {
    const match = history.location.pathname.match(/^\/experiences\/([^/]+)/);
    if (match && match[1] !== 'create') {
      return { templateId: match[1] };
    }
    return null;
  }, [history.location.pathname]);

  // Template the build targeted — captured so "Continue" can hand the user
  // over to that template's validator (create flow: the new template; edit
  // flow: the page's template).
  const builtTemplateIdRef = useRef<string | null>(null);

  // Handle actions from the AI (navigate on template creation, show toasts)
  const handleActions = useCallback((actions: ToolAction[]) => {
    setLastActions(actions);

    // Auto-navigate to newly created template
    const created = actions.find((a) => a.tool === 'draft_experience_template');
    const newTemplateId = created?.result.templateId;
    const targetTemplateId =
      typeof newTemplateId === 'string' && newTemplateId
        ? newTemplateId
        : pageContext?.templateId ?? null;
    builtTemplateIdRef.current = targetTemplateId;
    if (typeof newTemplateId === 'string' && newTemplateId) {
      // Land the user on the Timeline step of the edit view (the
      // `tasks` step is labeled "Timeline" in the UI) so they can start
      // editing what the copilot built — overview is a dead end here.
      setTimeout(() => {
        history.push(
          ROUTES.experiences.details.edit.url(newTemplateId, { step: 'tasks' })
        );
      }, 1500);
    }
  }, [history, ROUTES, pageContext]);

  const {
    messages, isLoading, sendMessage, submitFeedback, clearChat,
    conversations, isLoadingHistory, loadConversation, conversationId,
    getSessionSnapshot, hasWriteActionOccurred,
  } = useCopilotChat({ organizationId, pageContext, onActions: handleActions });

  const { template, isLoading: isTemplateLoading, clearPreview } = useTemplatePreview(messages);

  // Phase 2 observability: fire SESSION_STARTED on open, SESSION_ENDED
  // on close. Track session start timestamp + previous-isOpen so we
  // detect the transition (not every render).
  const aiObs = useAIObservability();
  const sessionStartedAtRef = useRef<number | null>(null);
  const prevIsOpenRef = useRef<boolean>(false);
  useEffect(() => {
    if (prevIsOpenRef.current === isOpen) return;
    prevIsOpenRef.current = isOpen;
    if (isOpen) {
      // Opening — emit STARTED. Use conversationId from useCopilotChat
      // if present (resumed session), else 'pending' (the first prompt
      // will allocate one server-side).
      sessionStartedAtRef.current = performance.now();
      aiObs.trackCopilotSessionStarted(conversationId ?? 'pending', {
        templateId: pageContext?.templateId,
        pageContext: pageContext?.templateId ? 'TemplateEdit' : 'Other',
        mode: pageContext?.templateId ? 'edit' : 'create',
      });
      return;
    }
    // Closing — emit ENDED with session stats snapshot.
    const startedAt = sessionStartedAtRef.current;
    const durationSec = startedAt
      ? Math.round((performance.now() - startedAt) / 1000)
      : 0;
    sessionStartedAtRef.current = null;
    const snap = getSessionSnapshot();
    aiObs.trackCopilotSessionEnded(snap.conversationId ?? 'pending', {
      totalTurns: snap.totalTurns,
      totalToolCalls: snap.totalToolCalls,
      totalTokens: snap.totalTokens,
      durationSec,
      exitReason: 'drawer-close',
      templateId: pageContext?.templateId,
    });
  }, [isOpen, aiObs, conversationId, pageContext, getSessionSnapshot]);

  const handleClose = useCallback(() => {
    if (ctx) ctx.close();
    else setLocalIsOpen(false);
  }, [ctx]);
  const handleNewChat = useCallback(() => {
    clearChat();
    clearPreview();
    setLastActions([]);
  }, [clearChat, clearPreview]);
  // "Continue" after the build log: hand the user over to the validator
  // instead of reverting to chat. Close the panel and re-assert the edit
  // URL with checkChanges=1 — TemplateEdit picks that up and auto-runs
  // Check Changes (the only real entry to the validator). Reopening Copilot
  // is one click on the nav pill. We re-navigate (rather than just setting
  // the param on the current URL) so the handover survives the race where a
  // tiny build completes before the 1.5s auto-redirect.
  const handleBuildingDone = useCallback(() => {
    setLastActions([]);
    if (ctx) ctx.close();
    else setLocalIsOpen(false);
    const tid = builtTemplateIdRef.current;
    if (tid) {
      const base = ROUTES.experiences.details.edit.url(tid, { step: 'tasks' });
      // Append raw param — same convention openValidationItem uses for
      // &t=/&a=/&m=. `base` always carries ?step=&orgId= so the & is safe.
      history.push(`${base}&checkChanges=1`);
    }
  }, [ctx, ROUTES, history]);

  // Show split-pane when a template is being previewed
  const showBuilder = template !== null || isTemplateLoading;

  return (
    <>
      {showBuilder ? (
        // Builder view (split-pane preview) keeps its existing centered/popup
        // behavior — slide animation is for the chat-only path.
        isOpen && (
          <ExperienceBuilderView
            messages={messages}
            isLoading={isLoading}
            onSend={sendMessage}
            onClose={handleClose}
            onNewChat={handleNewChat}
            template={template}
            isTemplateLoading={isTemplateLoading}
            conversations={conversations}
            isLoadingHistory={isLoadingHistory}
            activeConversationId={conversationId}
            onLoadConversation={loadConversation}
            submitFeedback={submitFeedback}
          />
        )
      ) : (
        // direction="left" = slides leftward, i.e. enters from the right edge
        // and exits back to the right. mountOnEnter/unmountOnExit keep the DOM
        // clean when closed.
        <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
          <ChatWindow
            messages={messages}
            isLoading={isLoading}
            onSend={sendMessage}
            onClose={handleClose}
            onNewChat={handleNewChat}
            conversations={conversations}
            isLoadingHistory={isLoadingHistory}
            activeConversationId={conversationId}
            onLoadConversation={loadConversation}
            pageContext={pageContext}
            buildingActions={lastActions}
            onBuildingDone={handleBuildingDone}
            submitFeedback={submitFeedback}
            hasWriteActionOccurred={hasWriteActionOccurred}
          />
        </Slide>
      )}
    </>
  );
};
